"use client"

import { useEffect, useRef, useState } from "react"
import "./Hiring.css"

const API_URL = "https://api.katchit.in/api/v1/Hiring/Apply"

const jobs = [
  {
    title: "Sales Executive",
    department: "Sales",
    openings: 2,
    icon: "bi-graph-up-arrow",
    description: "Build customer relationships, understand their needs and help grow Katchit's services.",
  },
  {
    title: "Data Entry Executive",
    department: "Operations",
    openings: 1,
    icon: "bi-keyboard",
    description: "Maintain accurate business records, listings and day-to-day operational data.",
  },
  {
    title: "Telecaller / Customer Support Executive",
    department: "Customer Experience",
    openings: 2,
    icon: "bi-headset",
    description: "Connect with customers and vendors, resolve queries and provide a helpful experience.",
  },
  {
    title: "Lead Generation Executive",
    department: "Business Development",
    openings: 2,
    icon: "bi-bullseye",
    description: "Research prospects, create qualified leads and support the business development team.",
  },
  {
    title: "Graphic Designer",
    department: "Creative",
    openings: 1,
    icon: "bi-palette",
    description: "Create engaging brand, social media and campaign visuals across Katchit's platforms.",
  },
  {
    title: "Operations Manager",
    department: "Management",
    openings: 1,
    icon: "bi-diagram-3",
    description: "Coordinate teams, improve daily workflows and ensure smooth execution across business operations.",
  },
  {
    title: "Software Tester",
    department: "Technology",
    openings: 2,
    icon: "bi-bug",
    description: "Test web and mobile experiences, report issues clearly and help us deliver reliable products.",
  },
]

const positions = [...jobs.map(job => job.title), "Other"]
const initialForm = {
  fullName: "",
  email: "",
  phoneNumber: "",
  city: "",
  position: "",
  experienceYears: "",
  currentOccupation: "",
  coverNote: "",
}

export default function Hiring() {
  const firstInputRef = useRef(null)
  const formRef = useRef(null)
  const [showApplication, setShowApplication] = useState(false)
  const [form, setForm] = useState(initialForm)
  const [resume, setResume] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [notice, setNotice] = useState({ type: "", message: "" })

  useEffect(() => {
    if (!showApplication) return undefined

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    window.setTimeout(() => firstInputRef.current?.focus(), 100)

    const closeOnEscape = event => {
      if (event.key === "Escape" && !submitting) setShowApplication(false)
    }
    window.addEventListener("keydown", closeOnEscape)

    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener("keydown", closeOnEscape)
    }
  }, [showApplication, submitting])

  const update = event => setForm(current => ({ ...current, [event.target.name]: event.target.value }))

  const openApplication = (position = "") => {
    setForm(current => ({ ...current, position }))
    setNotice({ type: "", message: "" })
    setShowApplication(true)
  }

  const closeApplication = () => {
    if (!submitting) setShowApplication(false)
  }

  const submit = async event => {
    event.preventDefault()
    setSubmitting(true)
    setNotice({ type: "", message: "" })

    try {
      const payload = new FormData()
      Object.entries(form).forEach(([key, value]) => {
        if (value !== "") payload.append(key, value)
      })
      if (resume) payload.append("resume", resume)

      const response = await fetch(API_URL, { method: "POST", body: payload })
      const result = await response.json().catch(() => ({}))
      if (!response.ok || result.status === false) {
        throw new Error(result.message || "Application could not be submitted.")
      }

      setNotice({
        type: "success",
        message: "Application submitted successfully. Our team will contact you if your profile is shortlisted.",
      })
      setForm(initialForm)
      setResume(null)
      formRef.current?.reset()
    } catch (error) {
      setNotice({ type: "error", message: error.message || "Application could not be submitted. Please try again." })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section className="hiring-section">
      <div className="hiring-orb hiring-orb-one" />
      <div className="hiring-orb hiring-orb-two" />

      <div className="hiring-page-container">
        <header className="hiring-hero">
          <span className="hiring-eyebrow"><i className="bi bi-stars" /> Careers at Katchit</span>
          <h1>Find your place at Katchit.</h1>
          <p>Join a fast-moving team building better local experiences for customers, vendors and delivery partners.</p>
          <button type="button" className="hiring-primary-button" onClick={() => openApplication()}>
            Apply Now <i className="bi bi-arrow-right" />
          </button>
        </header>

        <div className="hiring-list-heading">
          <div>
            <span>Current opportunities</span>
            <h2>Open Positions</h2>
          </div>
          <p>{jobs.reduce((total, job) => total + job.openings, 0)} openings across {jobs.length} roles</p>
        </div>

        <div className="hiring-jobs-grid">
          {jobs.map(job => (
            <article className="hiring-job-card" key={job.title}>
              <div className="hiring-job-topline">
                <span className="hiring-job-icon"><i className={`bi ${job.icon}`} /></span>
                <span className="hiring-opening-badge">
                  <i className="bi bi-people" /> {job.openings} {job.openings === 1 ? "opening" : "openings"}
                </span>
              </div>
              <span className="hiring-department">{job.department}</span>
              <h3>{job.title}</h3>
              <p>{job.description}</p>
              <div className="hiring-job-footer">
                <div className="hiring-job-meta">
                  <span><i className="bi bi-clock" /> Full-time / Part-time</span>
                  <span><i className="bi bi-person-check" /> Fresher / Experienced</span>
                  <span><i className="bi bi-laptop" /> WFH / WFO</span>
                  <span><i className="bi bi-geo-alt" /> Anywhere</span>
                </div>
                <button type="button" onClick={() => openApplication(job.title)}>
                  Apply <i className="bi bi-arrow-up-right" />
                </button>
              </div>
            </article>
          ))}
        </div>

        <div className="hiring-other-role">
          <div className="hiring-other-icon"><i className="bi bi-send" /></div>
          <div>
            <h3>Can't find your profile?</h3>
            <p>Send us your details. We will keep your profile in mind for future opportunities.</p>
          </div>
          <button type="button" onClick={() => openApplication("Other")}>Submit your profile</button>
        </div>
      </div>

      {showApplication && (
        <div className="hiring-modal-backdrop" role="presentation" onMouseDown={event => {
          if (event.target === event.currentTarget) closeApplication()
        }}>
          <div className="hiring-modal" role="dialog" aria-modal="true" aria-labelledby="hiring-modal-title">
            <div className="hiring-modal-header">
              <div className="hiring-form-heading">
                <span><i className="bi bi-briefcase" /></span>
                <div>
                  <h3 id="hiring-modal-title">Apply to Katchit</h3>
                  <p>Share your details and our hiring team will get in touch.</p>
                </div>
              </div>
              <button type="button" className="hiring-modal-close" onClick={closeApplication} aria-label="Close application form">
                <i className="bi bi-x-lg" />
              </button>
            </div>

            <form ref={formRef} className="hiring-form-card" onSubmit={submit}>
              <div className="hiring-form-grid">
                <label>Full name<input ref={firstInputRef} name="fullName" value={form.fullName} onChange={update} placeholder="Your full name" maxLength="150" required /></label>
                <label>Email address<input name="email" type="email" value={form.email} onChange={update} placeholder="you@example.com" maxLength="254" required /></label>
                <label>Phone number<input name="phoneNumber" value={form.phoneNumber} onChange={update} placeholder="+91 98765 43210" maxLength="25" required /></label>
                <label>City<input name="city" value={form.city} onChange={update} placeholder="Your city" maxLength="100" required /></label>
                <label className="hiring-full">Position<select name="position" value={form.position} onChange={update} required><option value="">Select a position</option>{positions.map(position => <option key={position} value={position}>{position}</option>)}</select></label>
                <label>Experience (years)<input name="experienceYears" type="number" min="0" max="60" step="0.5" value={form.experienceYears} onChange={update} placeholder="e.g. 2" /></label>
                <label>Current role<input name="currentOccupation" value={form.currentOccupation} onChange={update} placeholder="Current role or student" maxLength="150" /></label>
                <label className="hiring-full">Why would you like to join?<textarea name="coverNote" value={form.coverNote} onChange={update} placeholder="Share your interests and strengths..." maxLength="2000" rows="4" /></label>
                <label className="hiring-full hiring-file">Resume <span>(optional, PDF/DOC/DOCX up to 5 MB)</span><input type="file" accept=".pdf,.doc,.docx" onChange={event => setResume(event.target.files?.[0] || null)} /></label>
              </div>
              {notice.message && <div className={`hiring-notice ${notice.type}`} role="status"><i className={`bi ${notice.type === "success" ? "bi-check-circle" : "bi-exclamation-circle"}`} /> {notice.message}</div>}
              <button className="hiring-submit" disabled={submitting}>{submitting ? <><span className="hiring-spinner" /> Submitting...</> : <>Submit Application <i className="bi bi-send" /></>}</button>
              <p className="hiring-privacy"><i className="bi bi-shield-check" /> Your information is used only for recruitment purposes.</p>
            </form>
          </div>
        </div>
      )}
    </section>
  )
}
